import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GenericFormScene } from 'shared-ui/lib/components/generic-form-scene/GenericFormScene';
import { parseGraphqlErrors } from 'shared-ui/lib/utils/get-gql-error';
import { useParams } from 'shared-ui/lib/utils/use-params';
import { ConfirmModal } from '../../components/ConfirmModal';
import { routesConfig } from '../../config/routes.config';
import {
  serviceEventFormAll,
  serviceEventFormConsumer,
  ServiceEventFormBody,
} from '../../form/service-event/service-event';
import { useCreateServiceEventMutation } from '../../generated/graphql';
import { useUploadFile } from '../../hooks/use-upload-file';
import { GraphQLError } from 'graphql';
import UserContext from '../../contexes/UserContext';

export const ServiceEventCreateScene = (): JSX.Element => {
  const { t } = useTranslation('ServiceEventCreateScene');
  const navigate = useNavigate();
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const { uploadFile, isImageLoading } = useUploadFile({ type: 'service-event-image' });
  const [user] = useContext(UserContext);
  const [isAttachmentsPromptOpen, setIsAttachmentsPromptOpen] = useState(false);
  const [pendingShowUrl, setPendingShowUrl] = useState<string | null>(null);

  const [createServiceEvent, { loading }] = useCreateServiceEventMutation();

  const handleSubmit = async ({ photo, ...body }: ServiceEventFormBody) => {
    const { data, errors } = await createServiceEvent({
      variables: { body: { ...body, vehicleId } },
    });
    if (!data) throw new Error(parseGraphqlErrors(errors as GraphQLError[]));
    await uploadFile(photo, data.createServiceEvent.id);
    const showUrl = routesConfig.SERVICE_EVENTS.SHOW.replace(':vehicleId', vehicleId).replace(
      ':id',
      data.createServiceEvent.id
    );
    setPendingShowUrl(showUrl);
    setIsAttachmentsPromptOpen(true);
  };

  const finishNavigate = (openAttachments: boolean) => {
    if (!pendingShowUrl) return;
    const url = pendingShowUrl;
    setIsAttachmentsPromptOpen(false);
    setPendingShowUrl(null);
    navigate(
      url,
      openAttachments ? { state: { openServiceEventAttachments: true } } : undefined
    );
  };

  const isConsumerUser = user.type === 'consumer' && !user.isBusinessAccountOwner;

  const form = isConsumerUser ? serviceEventFormConsumer : serviceEventFormAll;

  return (
    <>
      <GenericFormScene
        blocker={(event) => {
          if (confirm(t('confirmLeave')) === false) {
            event.preventDefault();
          }
        }}
        type="create"
        title={t('addServiceEvent')}
        onSubmit={handleSubmit}
        form={form}
        isSubmitLoading={loading || isImageLoading}
        backLink={routesConfig.VEHICLES.SHOW.replace(':id', vehicleId)}
        preFooterElement={
          <div className="mt-10 mb-10 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-color">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-primary flex-shrink-0 mt-0.5"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <p>{t('attachmentsAfterCreateInfo')}</p>
          </div>
        }
      />
      <ConfirmModal
        isOpen={isAttachmentsPromptOpen}
        title={t('addAttachmentsPromptTitle')}
        variant="success"
        confirmLabel={t('addAttachmentsPromptYes')}
        cancelLabel={t('addAttachmentsPromptNo')}
        onConfirm={() => finishNavigate(true)}
        onCancel={() => finishNavigate(false)}
      />
    </>
  );
};
